import React, { useCallback, useEffect, useState } from 'react'
import {
	change, Field, getFormValues, initialize, InjectedFormProps, reduxForm
} from 'redux-form'
import {
	Button, Col, Divider, Form, Modal, notification, Row, Select
} from 'antd'
import {
	BankOutlined, BorderOutlined, BuildOutlined, DeleteOutlined, EuroOutlined, FilePdfOutlined,
	FileProtectOutlined, FileTextOutlined, HomeOutlined, PlusOutlined, RiseOutlined, SaveOutlined, ShopOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { debounce, get, map } from 'lodash'
import i18next from 'i18next'
import { Link } from 'react-router-dom'
import {
	FORMS, FREQUENCIES, FrequencyI, PAGINATION, USER_ROLE
} from '../../../utils/enums'
import TextField from '../../../atoms/form/TextField'
import validateForm from './validateJobForm'
import {
	createUser, getUsers, IUserCreateParams, IUserUpdateParams
} from '../../../reducers/usersU/actions'
import { getTasks } from '../../../reducers/tasks/actions'
import CustomOptionsSelectField from '../../../atoms/form/CustomOptionsSelectField'
import { UsePicker } from '../../../components/useFrequenciesPicker'
import OptionUserSelectField from '../../../atoms/form/OptionUserSelectField'
import { getTrustees } from '../../../reducers/userTrustees/actions'
import DragAndDropField, { MoveOptionI, MoveOptions } from '../../../atoms/form/DragAndDropField'
import Collections from '../../../components/Collections'
import { divideArrayFromAnother } from '../../../utils/helper'
import { RootState } from '../../../reducers'
import {
	IJobDetail, IJobForm, IPriceOfferItem, IUserJobTask
} from '../../../reducers/jobs/actions'
import StoreItems from '../../storeItems/StoreItems'
// eslint-disable-next-line import/no-cycle
import { STORE_TYPE } from '../../stores/Stores'
import InputField from '../../../atoms/InputField'
import InputNumberField from '../../../atoms/InputNumberField'
import StyledLink from '../../../components/NavLink'
import UserForm from '../../users/forms/UserForm'
import SwitchField from '../../../atoms/SwitchField'
import { getPriceOfferTag } from '../../priceOffers/forms/PriceOfferForm'
import SelectPriceOfferHeaderOptionField, { ISelectedFirmHeaderOption } from '../../../components/SelectPriceOfferHeaderOptionField'
import { getPriceOfferHeaderOptions, IFirmHeader } from '../../../reducers/priceOffers/actions'
import { openPdfInNewTab } from '../../../utils/openPdf'
import HandoverProtocolsDrawer from '../../handoverProtocols/HandoverProtocolsDrawer'
import HandoverProtocolSidebarSection from '../../handoverProtocols/HandoverProtocolSidebarSection'
import { history } from '../../../utils/history'

const { Option } = Select
const PAGE_SIZE = 200

type ComponentProps = {
	onRemove: (id: number) => void,
	onRefresh: () => void,
	isCreate: boolean,
	id: number,
	transferJobToOffer: (id: number) => void
}

type Props = InjectedFormProps<IJobForm, ComponentProps> & ComponentProps

// NOTE: ikona sa vklada do labelu, nie ako prefix inputu - antd 4.15 InputNumber ani Select prefix
// nepodporuju, takze len takto vie mat kazde pole v Obchodnych informaciach ikonu rovnako
const labelWithIcon = (icon: React.ReactNode, text: string) => (
	<span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
		{icon}
		{text}
	</span>
)

const PriceOfferList = (jobDetail: IJobDetail) => jobDetail?.priceOffers?.map((priceOffer: IPriceOfferItem) => (
	<StyledLink
		to={`${i18next.t('paths:priceOfferDetail|path')}/${priceOffer?.id}`}
		key={priceOffer?.id}
		className={'w-max'}
		style={ {
			marginTop: '10px',
			display: 'flex',
			justifyContent: 'space-between'
		} }
	>
		<span className={'mr-5'}>{ getPriceOfferTag(priceOffer.status)}</span>
		<span>{ priceOffer?.createdAt }</span>
	</StyledLink>
))

function getTasksInTargetFrequencies(jobFormValues: IJobForm, fromFrequency: FrequencyI, toFrequency: FrequencyI): IUserJobTask[] {
	const tasks = get(jobFormValues, `${fromFrequency.key}Tasks`, [])
	const tasksInTargetFrequencies = get(jobFormValues, `${toFrequency.key}Tasks`, [])
	const combinedTasks = tasksInTargetFrequencies.concat(tasks)

	return combinedTasks.reduce((acc: IUserJobTask[], currentTask: IUserJobTask) => {
		if (!acc.find((task) => task.task.id === currentTask.task.id)) {
			acc.push({
				...currentTask,
				frequency: toFrequency.key.toUpperCase()
			})
		}
		return acc
	}, [])
}

const JobForm = ({
	handleSubmit,
	onRemove,
	onRefresh,
	invalid,
	pristine,
	isCreate,
	id,
	transferJobToOffer
}: Props) => {
	const dispatch = useDispatch()

	const [isModalCreateTrusteeVisible, setIsModalCreateTrusteeVisible] = useState(false)

	const jobFormValues: IJobForm = useSelector((state: RootState) => getFormValues(FORMS.JOB_FORM)(state)) as IJobForm
	const [isVisiblePriceOfferModal, setIsVisiblePriceOfferModal] = useState(false)
	const [isVisibleHandoverRecordModal, setIsVisibleHandoverRecordModal] = useState(false)
	const [isHandoverProtocolsDrawerVisible, setIsHandoverProtocolsDrawerVisible] = useState(false)

	const handleOk = () => {
		setIsModalCreateTrusteeVisible(false)
	}

	const handleCreatePriceOffer = () => {
		transferJobToOffer(id)
		setIsVisiblePriceOfferModal(false)
	}

	const [headerValue, setHeaderValue] = useState<ISelectedFirmHeaderOption | null | undefined>()

	const [headerOptions, setHeaderOptions] = useState<IFirmHeader[]>([])

	useEffect(() => {
		dispatch(getPriceOfferHeaderOptions((data) => {
			setHeaderOptions(data.headers)
		}))
	}, [])

	const priceOfferHeaderOptions = (headerOptions)?.map((option: IFirmHeader) => ({
		label: option.firmName,
		value: option.id
	})) || []

	const handleCreateHandoverRecord = (jobId: number, optionHeader: ISelectedFirmHeaderOption) => {
		const pdfURL = `/api/admin/jobs/${jobId}/serve/hand-over-record/pdf?optionHeaderId=${optionHeader?.value}`
		openPdfInNewTab(pdfURL)
		setIsVisibleHandoverRecordModal(false)
	}

	const handleCancel = () => {
		setIsModalCreateTrusteeVisible(false)
	}

	const handleCreate = (body: any) => {
		dispatch(createUser(body, (userID) => {
			setIsModalCreateTrusteeVisible(false)
			// {"id":10,"name":"Martin Malobický - test","sendEmail":false,"phone":"+421949810793","email":"malobicky.m@gmail.com"}
			const newTrustee = {
				id: userID,
				name: body.name,
				sendEmail: body.sendEmail || false,
				phone: body.phone,
				email: body.email
			}
			dispatch(change(FORMS.JOB_FORM, 'trustees', (jobFormValues?.trustees || []).concat([JSON.stringify(newTrustee) as any])))
		}, (userID) => {
			setIsModalCreateTrusteeVisible(false)
		}))
	}

	const handleCreateTrusteeSubmit = (values: IUserCreateParams | IUserUpdateParams) => {
		const body = {
			firstName: get(values, 'firstName'),
			lastName: get(values, 'lastName'),
			email: get(values, 'email'),
			phone: get(values, 'phone'),
			role: get(values, 'role')
		}

		handleCreate({
			...body,
			password: get(values, 'password')
		})
	}

	const dataSource = useSelector((state:RootState) => state.tasks.tasks.tableData)

	const hasPriceForCleaningFeature = useSelector((state: RootState) => state.profile.profile.data?.hasPriceForCleaningFeature)

	const detail = useSelector((state:RootState) => state.jobs.detail)
	// pracovnik priradeny na zakazku - jeho osobny sklad sleduje druha sekcia v bocnom paneli
	const assignedUserID = detail?.data?.user?.id ?? null
	const trustees = useSelector((state:RootState) => state.userTrustees.trustees.tableData)

	const [trusteeFilter, setTrusteeFilter] = useState({})

	const users = useSelector((state:RootState) => state.users.users.tableData)
		.map((user: any) => {
			const userName = user.lastName ? `${user.lastName} ${user.firstName}` : user.email
			return {
				value: user.id,
				label: userName
			}
		})
	const {
		filter,
		Picker
	} = UsePicker()
	// const [filter, setFilter] = useState({})

	useEffect(() => {
		dispatch(getTasks(
			1,
			PAGINATION.defaultPageSize
		))
	}, [])

	useEffect(() => {
		dispatch(getUsers(
			1,
			10000,
			{
				role: [USER_ROLE.USER, USER_ROLE.ADMIN, USER_ROLE.SUPERADMIN]
			}
		))
	}, [])

	useEffect(() => {
		const body = {
			limit: PAGINATION.defaultPageSize,
			page: 1,
			...trusteeFilter
		}
		dispatch(getTrustees(body))
	}, [trusteeFilter, dispatch])

	const { t } = useTranslation('components')
	const filteredItems: any[] = divideArrayFromAnother(FREQUENCIES, filter)
	const items = filteredItems.map((value: any) => (
		<div
			key={value?.key}
			className={'mt-6'}
			style={ {
				justifySelf: 'start',
				width: '100%',
				marginBottom: 40
			} }
		>
			<Field
				name={ `${value.key}Tasks` }
				extraData={ detail?.data?.extras?.[value?.key?.toUpperCase()] }
				options={ dataSource }
				handleRefresh={ onRefresh }
				frequency={ value.key.toUpperCase() }
				jobId={ id }
				userId={ jobFormValues?.userID }
				allowMoveDataToOtherFrequency={true}
				component={ DragAndDropField }
				onMoveTasks={(fromFrequency: FrequencyI, toFrequency: FrequencyI, moveOption: MoveOptionI) => {
					switch (moveOption.key.toLowerCase()) {
						case MoveOptions.copy.toLowerCase(): {
							const tasksInTargetFrequencies = getTasksInTargetFrequencies(jobFormValues, fromFrequency, toFrequency)
							dispatch(change(FORMS.JOB_FORM, `${toFrequency.key}Tasks`, tasksInTargetFrequencies))
							break
						}
						case MoveOptions.move.toLowerCase(): {
							const tasksInTargetFrequencies = getTasksInTargetFrequencies(jobFormValues, fromFrequency, toFrequency)
							dispatch(change(FORMS.JOB_FORM, `${fromFrequency.key}Tasks`, []))
							dispatch(change(FORMS.JOB_FORM, `${toFrequency.key}Tasks`, tasksInTargetFrequencies))
							break
						}
						default: {
							const tasksInTargetFrequencies = getTasksInTargetFrequencies(jobFormValues, fromFrequency, toFrequency)
							dispatch(change(FORMS.JOB_FORM, `${toFrequency.key}Tasks`, tasksInTargetFrequencies))
						}
					}
				}
				}
				label={ value.name }
				required
			/>
		</div>
	))

	const debounced = useCallback(debounce((searchTerm: string) => {
		setTrusteeFilter({ search: searchTerm, page: 1 })
	}, 300), [trusteeFilter])

	const handleOnChange = (searchTerm: string) => {
		debounced(searchTerm)
	}

	return (
		<Form layout={'vertical'} onSubmitCapture={ handleSubmit }>
			<Row gutter={ [16, 16] }>
				<Col xs={ 24 } xl={ 18 } className={ 'grid' }>
					<div style={ { height: '100%' } } className={ 'flex direction-col justify-start main-content' }>
						<h3>{ t('pages:Pridať úlohu') }</h3>
						<Row gutter={ 8 } justify={ 'start' }>
							<Col className={ 'gutter-row' } xs={ 24 } sm={ 12 } xl={ 6 }>
								<Field
									name={ 'userID' }
									component={ CustomOptionsSelectField }
									label={ t('components:Pracovník') }
									options={ map(users, (option) => (
										<Option
											key={ option.value }
											value={ option.value }
										>
											{ option.label }
										</Option>
									)) }
									size={ 'large' }
									allowClear
									showSearch
									filterOption={ (input: any, option: any) => String(option?.children ?? '').toLowerCase()
										.indexOf(input.toLowerCase()) >= 0 }
								/>

							</Col>
							<Col className={ 'gutter-row' } xs={ 24 } sm={ 12 } xl={ 6 }>
								<Field
									name={ 'address' }
									component={ InputField }
									label={ t('components:Adresa') }
									size={ 'large' }
									required
								/>
							</Col>
							{/* Removed this functionality is now obsolete, ideme to pocitat s janom inak */}
							{/* {hasPriceForCleaningFeature && <Col className={'gutter-row'} span={6}> */}
							{/*	<Field */}
							{/*		name={'priceForCleaning'} */}
							{/*		component={InputNumberField} */}
							{/*		label={t('components:Cena za jedno upratovanie (€)')} */}
							{/*		size={'large'} */}
							{/*		type={'number'} */}
							{/*	/> */}
							{/* </Col>} */}
							<Col className={ 'gutter-row' } span={ 24 }>
								<Field
									name={ 'trustees' }
									component={ OptionUserSelectField }
									label={
										<span className={'mt-1 mb-2'}>
											<span className={''}>{t('components:Zodpovedné osoby')}</span>
											{
												jobFormValues?.priceOfferTrustee && (
													<Button
														icon={<SaveOutlined />}
														onClick={() => {
															const initValues = {
																firstName: jobFormValues?.priceOfferTrustee?.name,
																role: USER_ROLE.JOB_TRUSTEE,
																lastName: '',
																contactEmail: jobFormValues?.priceOfferTrustee?.emailOfTrustee,
																email: jobFormValues?.priceOfferTrustee?.emailOfTrustee,
																phone: jobFormValues?.priceOfferTrustee?.phoneOfTrustee,
																isConfirmed: true
															}
															dispatch(initialize(FORMS.USER_FORM, initValues))
															setIsModalCreateTrusteeVisible(true)
														}}
														disabled={false}
														type={'primary'}
														className={'ml-2'}
													>
														<span>{t('pages:Vytvoriť klienta ')}</span>
														<span className={'font-semibold'}>&nbsp;{jobFormValues?.priceOfferTrustee?.name}&nbsp;{jobFormValues?.priceOfferTrustee?.emailOfTrustee}&nbsp;</span>
														<span>{t('pages: z cenovej ponuky')}</span>
													</Button>
												)
											}
										</span>
									}
									options={map(trustees.filter((data: any) => {
										const trustees = jobFormValues?.trustees
										const found = trustees?.find((trustee: any) => {
											try {
												return JSON.parse(trustee).id === data?.id
											} catch (e) {
												return false
											}
										})
										return !found
									}), (option) => (
										<Option
											key={ option.id }
											value={ JSON.stringify({
												id: option.id,
												name: option.name,
												sendEmail: false,
												phone: option.phone,
												email: option.email
											}) }
											title={ option.name }
										>
											{ option.name }
										</Option>
									)) }
									onFocus={(data: any) => {
										setTrusteeFilter({ search: '', page: 1 })
									}}
									size={ 'large' }
									allowClear
									showSearch
									onSearch={(searchTerm: any) => {
										handleOnChange(searchTerm.toLowerCase())
									}}
								/>
								<Field
									name={ 'note' }
									component={ TextField }
									rows={2}
									label={ t('components:Poznámka pre zamestnanca') }
									size={ 'large' }
									maxLength={500}
								/>
							</Col>
						</Row>
					</div>
				</Col>
				<Col xs={ 24 } xl={ 6 } className={ 'grid' }>
					<div className={ 'flex direction-col justify-between sidebar-content' }>
						<div className={'flex direction-col justify-center'}>
							{!isCreate && (
								<Field
									name={'updatedAt'}
									component={TextField}
									disabled
									label={t('components:Dátum poslednej úpravy')}
								/>
							)}
							<Button
								icon={<SaveOutlined />}
								onClick={handleSubmit}
								disabled={pristine || invalid}
								type={'primary'}
								style={{ margin: '0px' }}
							>
								{t('pages:Uložiť')}
							</Button>
							<HandoverProtocolSidebarSection
								title={i18next.t('components:Preberací protokol zákazky')}
								query={id ? { type: 'JOB', jobID: id } : null}
								onOpenPanel={() => setIsHandoverProtocolsDrawerVisible(true)}
								onOpenProtocol={() => setIsHandoverProtocolsDrawerVisible(true)}
								showRecipient
							/>
							<HandoverProtocolSidebarSection
								title={i18next.t('components:Preberací protokol na osobné veci')}
								query={assignedUserID ? { type: 'PERSONAL', userID: assignedUserID } : null}
								/* osobny sklad patri profilu zamestnanca - detail zakazky nema jeho
								   userStoreId, takze panel sa otvara tam, nie tu */
								onOpenPanel={() => assignedUserID && history.push(`${i18next.t('paths:userDetail|path')}/${assignedUserID}`)}
								onOpenProtocol={() => assignedUserID && history.push(`${i18next.t('paths:userDetail|path')}/${assignedUserID}`)}
							/>
							<Divider style={{ marginTop: '10px', marginBottom: '10px' }}/>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center'
								}}
								className={'font-bold'}
							>
								{i18next.t('components:Cenové ponuky')}
								<PlusOutlined
									onClick={() => {
										setIsVisiblePriceOfferModal(true)
									}}
								/>
							</div>
							{detail?.data ? PriceOfferList(detail.data) : <div>{i18next.t('components:Žiadne cenové ponuky')}</div>}
							<div className={'mt-5'}></div>
						</div>
						<Button
							icon={<DeleteOutlined />}
							style={{ marginLeft: '0px' }}
							type={'primary'}
							onClick={() => onRemove(id)}
						>
							{t('components:Odstrániť zákazku')}
						</Button>
					</div>
				</Col>
			</Row>
			<Row gutter={16} className={'mt-5'}>
				<Col span={ 24 } className={ 'grid' }>
					<div style={ { height: '100%' } } className={ 'flex direction-col justify-start main-content' }>
						<h3>{ t('pages:Obchodné informácie') }</h3>
						<Row gutter={0} justify={'start'} className={'fields-container'}>
							<div className={'gutter-row'}>
								<Field
									name={'flats'}
									component={InputField}
									label={labelWithIcon(<HomeOutlined />, t('components:Počet bytov'))}
									size={'large'}
									type={'number'}
									min={0}
									precision={0}
									step={1}
								/>
							</div>
							<div className={'gutter-row'}>
								<Field
									name={'floors'}
									component={InputField}
									label={labelWithIcon(<BuildOutlined />, t('components:Počet poschodí'))}
									size={'large'}
									type={'number'}
									min={0}
									precision={0}
									step={1}
								/>
							</div>
							<div className={'gutter-row'}>
								<Field
									name={'windows'}
									component={InputField}
									label={labelWithIcon(<BorderOutlined />, t('components:Počet okien'))}
									size={'large'}
									type={'number'}
									min={0}
									precision={0}
									step={1}
								/>
							</div>
							<div className={'gutter-row'}>
								<Field
									name={'nameOfHousingAssociation'}
									component={InputField}
									label={labelWithIcon(<BankOutlined />, t('components:Bytové družstvo'))}
									size={'large'}
								/>
							</div>
							<div className={'gutter-row'}>
								<Field
									name={'priceFlatPerMonth'}
									component={InputNumberField}
									label={labelWithIcon(<EuroOutlined />, t('components:Cena za byt / mesiac (€)'))}
									size={'large'}
									type={'number'}
								/>
							</div>
						</Row>
						<Row gutter={0} justify={'start'} className={'fields-container'}>
							<div className={'gutter-row'}>
								<Field
									name={'priceOfferFirmHeaderID'}
									component={CustomOptionsSelectField}
									label={labelWithIcon(<ShopOutlined />, t('loc:Hlavička firmy'))}
									placeholder={t('loc:Vyber hlavičku firmy')}
									options={map(priceOfferHeaderOptions, (option) => (
										<Option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</Option>
									))}
									size={'large'}
									allowClear
									showSearch
									filterOption={(input: any, option: any) => String(option?.children ?? '').toLowerCase()
										.indexOf(input.toLowerCase()) >= 0}
								/>
							</div>
							<div className={'gutter-row'}>
								<Field
									name={'comment'}
									component={InputField}
									label={labelWithIcon(<FileTextOutlined />, t('components:Poznámka'))}
									placeholder={t('components:Poznámka')}
									size={'large'}
									type={'text'}
								/>
							</div>
						</Row>
						<Row gutter={0} justify={'start'} className={'fields-container'}>
							<div className={''}>
								<Field
									name={'inflationPartAccepted'}
									component={SwitchField}
									label={labelWithIcon(<RiseOutlined />, t('components:Akceptovaná inflačná doložka'))}
									size={'large'}
								/>
							</div>
						</Row>
					</div>
				</Col>
			</Row>

			<div className={'jobs-container'}>
				{id && <Collections id={id} />}
			</div>
			<Picker />
			<div className={'jobs-container'}>
				{items}
			</div>
			{!isCreate && (
				<Row >
					<Col className={'grid'}>
						<div className={'flex justify-between align-center'}>
							<h3 className={'ml-2'}>{t('pages:Skladové položky')}</h3>
							<div className={'flex align-center'}>
								<Button
									icon={<FilePdfOutlined />}
									onClick={() => {
										setIsVisibleHandoverRecordModal(true)
									}}
								>
									{t('pages:Preberací protokol')}
								</Button>
								<Button
									className={'ml-2'}
									type={'primary'}
									icon={<FileProtectOutlined />}
									onClick={() => setIsHandoverProtocolsDrawerVisible(true)}
								>
									{t('pages:Podpísané protokoly')}
								</Button>
							</div>
						</div>
						<StoreItems
							tableData={ null }
							initFilter={
								{ jobID: id, jobToID: null }
							}
							// materiál sa na zákazku štandardne posiela, nie odoberá - preto je zákazka predvolene príjemca
							contextStore={ jobFormValues?.address ? { id, label: jobFormValues.address, defaultSide: 'TO', storeType: STORE_TYPE.JOB_STORE } : null }
							tableMode={true}
							addRemoveItemFeature={true}
						/>
					</Col>
				</Row>
			) }
			<Modal title={ i18next.t('pages:Vytvoriť klienta') } visible={isModalCreateTrusteeVisible} okText={i18next.t('pages:Vytvoriť')} onOk={handleOk} onCancel={handleCancel} footer={null} >
				<UserForm
					onSubmit={ handleCreateTrusteeSubmit }
					isCreate={ true }
					trusteePopup={true}
					userID={null}
					setShowPasswordChangeModal={() => {}}
					onRefreshPage={() => {}}
				/>
			</Modal>
			<Modal title={ i18next.t('pages:Vytvoriť cenovú ponuku') } visible={isVisiblePriceOfferModal} okText={i18next.t('pages:Vytvoriť')} onOk={handleCreatePriceOffer} onCancel={ () => setIsVisiblePriceOfferModal(false)} >
				{ i18next.t('pages:Vytvoriť cenovú ponuku z aktualnej zákazky') }
			</Modal>
			<Modal
				title={ i18next.t('pages:Vytvoriť preberací protokol') }
				visible={isVisibleHandoverRecordModal}
				okText={i18next.t('pages:Vytvoriť')}
				onOk={() => {
					if (!headerValue) {
						notification.error({
							message: i18next.t('pages:Vyberte hlavičku')
						})
						return
					}
					handleCreateHandoverRecord(id, headerValue)
				}}
				onCancel={ () => setIsVisibleHandoverRecordModal(false)} >
				<div className={'mb-4'}>
					<span className={'mr-2'}>{i18next.t('pages:Chýbajúce údaje môžete doplniť v nastaveniach')}</span>
					<Link to={ t('paths:appSettings|path').toString()}>
						{ i18next.t('pages:Nastavenia') }
					</Link>
				</div>
				<SelectPriceOfferHeaderOptionField
					onValueChange={setHeaderValue}
					headerValue={headerValue}
				/>
			</Modal>
			{!isCreate && (
				<HandoverProtocolsDrawer
					visible={isHandoverProtocolsDrawerVisible}
					onClose={() => setIsHandoverProtocolsDrawerVisible(false)}
					type={'JOB'}
					jobID={id}
					title={jobFormValues?.address || ''}
					storeItemsProps={{ tableData: null, initFilter: { jobID: id, jobToID: null } }}
					defaultRecipientUserID={detail?.data?.user?.id ?? null}
					defaultRecipientName={detail?.data?.user ? `${detail.data.user.firstName || ''} ${detail.data.user.lastName || ''}`.trim() : null}
				/>
			)}
		</Form>
	)
}

const form = reduxForm<IJobForm, ComponentProps>({
	form: FORMS.JOB_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateForm
})(JobForm)

export default form
